import { Check, CheckCheck, FileText, Download, MapPin, Contact, BarChart3, Clock } from "lucide-react";
import { formatarTelefone } from "../services/whatsappChatApi";

const ORIGEM_LABEL = {
  automacao: "Automação",
  agente: "Agente",
  campanha: "Campanha",
  apolo: "Apolo",
  chat: "Chat",
  api: "API",
  celular: "Celular",
};

function formatarHora(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function formatarTamanho(bytes) {
  const n = Number(bytes);
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function timestampMsg(m) {
  return m?.timestamp_wa ?? m?.criado_em ?? null;
}

function Ticks({ mensagem }) {
  if (mensagem._otimista) return <Clock className="w-3 h-3 text-ink-4" aria-label="Enviando" />;
  const ack = mensagem.ack ?? null;
  const lido = (ack !== null && ack >= 3) || Boolean(mensagem.lido_em);
  const entregue = (ack !== null && ack >= 2) || Boolean(mensagem.entregue_em);
  if (lido) return <CheckCheck className="w-3.5 h-3.5 text-accent-sky" aria-label="Lida" />;
  if (entregue) return <CheckCheck className="w-3.5 h-3.5 text-ink-4" aria-label="Entregue" />;
  if (ack === 0) return <Clock className="w-3 h-3 text-ink-4" aria-label="Pendente" />;
  return <Check className="w-3.5 h-3.5 text-ink-4" aria-label="Enviada" />;
}

function Midia({ mensagem }) {
  const { tipo, midia_url: url, midia_mime: mime, midia_nome: nome, midia_tamanho: tamanho, texto } = mensagem;

  if (tipo === "image" || tipo === "sticker") {
    if (!url) return <p className="text-xs text-ink-4 italic">[imagem indisponível]</p>;
    const sticker = tipo === "sticker";
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="block">
        <img
          src={url}
          alt={sticker ? "Figurinha" : "Imagem"}
          className={sticker ? "max-w-[140px] max-h-[140px] object-contain" : "rounded-lg max-w-full max-h-72 object-cover"}
          loading="lazy"
        />
      </a>
    );
  }
  if (tipo === "video") {
    if (!url) return <p className="text-xs text-ink-4 italic">[vídeo indisponível]</p>;
    return <video src={url} controls className="rounded-lg max-w-full max-h-72" preload="metadata" />;
  }
  if (tipo === "audio") {
    if (!url) return <p className="text-xs text-ink-4 italic">[áudio indisponível]</p>;
    return <audio src={url} controls className="max-w-full min-w-[220px]" preload="metadata" />;
  }
  if (tipo === "document") {
    return (
      <a
        href={url || "#"}
        target={url ? "_blank" : undefined}
        rel="noopener noreferrer"
        download={nome || undefined}
        className={`flex items-center gap-2.5 rounded-lg bg-surface-3 border border-white/[0.08] px-3 py-2 ${url ? "hover:border-white/[0.16]" : "opacity-60 cursor-default"} transition-colors`}
      >
        <FileText className="w-5 h-5 text-brand-300 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-xs text-ink-1 truncate">{nome || "Documento"}</p>
          <p className="text-[10px] text-ink-4">{[mime, formatarTamanho(tamanho)].filter(Boolean).join(" · ")}</p>
        </div>
        {url && <Download className="w-4 h-4 text-ink-3 flex-shrink-0" />}
      </a>
    );
  }
  if (tipo === "location") {
    return (
      <div className="flex items-center gap-2 text-xs text-ink-2">
        <MapPin className="w-4 h-4 text-accent-rose flex-shrink-0" />
        <span>{texto || "Localização compartilhada"}</span>
      </div>
    );
  }
  if (tipo === "contacts") {
    return (
      <div className="flex items-center gap-2 text-xs text-ink-2">
        <Contact className="w-4 h-4 text-accent-sky flex-shrink-0" />
        <span>{texto || "Contato compartilhado"}</span>
      </div>
    );
  }
  if (tipo === "poll") {
    return (
      <div className="flex items-center gap-2 text-xs text-ink-2">
        <BarChart3 className="w-4 h-4 text-accent-amber flex-shrink-0" />
        <span>{texto || "Enquete"}</span>
      </div>
    );
  }
  if (tipo === "interactive") {
    return <p className="text-xs text-ink-2">{texto || "Resposta interativa"}</p>;
  }
  if (tipo !== "text") {
    return <p className="text-xs text-ink-4 italic">{texto || `[${tipo || "mensagem"}]`}</p>;
  }
  return null;
}

const COM_MIDIA = new Set(["image", "video", "audio", "document", "sticker"]);

export default function MessageBubble({ mensagem, grupo }) {
  const eu = mensagem.direcao === "outbound";
  const ts = timestampMsg(mensagem);
  const temMidia = COM_MIDIA.has(mensagem.tipo);
  const mostraTexto = mensagem.tipo === "text" || (temMidia && mensagem.texto);
  const origem = eu && mensagem.origem ? ORIGEM_LABEL[mensagem.origem] || mensagem.origem : null;
  const autor = !eu && grupo ? mensagem.autor_nome || formatarTelefone(mensagem.autor_telefone) : null;

  return (
    <div className={`flex flex-col ${eu ? "items-end" : "items-start"} my-0.5 px-3`}>
      <div className="relative max-w-[78%] md:max-w-[65%]">
        <div
          className={`relative rounded-xl border px-3 py-2 shadow-sm ${
            eu ? "bg-brand-900/70 border-brand-800/70" : "bg-surface-2 border-white/[0.08]"
          }`}
          role="article"
          aria-label={eu ? "Mensagem enviada" : "Mensagem recebida"}
        >
          {autor && <p className="text-[11px] font-semibold text-brand-300 mb-0.5 truncate">{autor}</p>}

          {mensagem.tipo !== "text" && (
            <div className={mostraTexto ? "mb-1.5" : ""}>
              <Midia mensagem={mensagem} />
            </div>
          )}

          {mostraTexto && mensagem.texto && (
            <p className="text-xs text-ink-1 whitespace-pre-wrap leading-relaxed break-words">{mensagem.texto}</p>
          )}

          {mensagem.tipo === "text" && !mensagem.texto && (
            <p className="text-xs text-ink-4 italic">[mensagem vazia]</p>
          )}

          <div className={`flex items-center gap-1.5 mt-1 ${eu ? "justify-end" : "justify-start"}`}>
            {origem && (
              <span className="text-[9px] uppercase tracking-wider font-medium text-ink-4 bg-white/[0.04] ring-1 ring-white/[0.06] rounded px-1 py-px select-none">
                {origem}
              </span>
            )}
            <span className="text-[10px] text-ink-4 leading-none select-none tabular-nums">{formatarHora(ts)}</span>
            {eu && <Ticks mensagem={mensagem} />}
          </div>
        </div>
      </div>
    </div>
  );
}
